import { Box, Flex, Image, Tooltip, Text, useColorModeValue } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import React from 'react';

import type { Block } from 'types/api/block';

import config from 'configs/app';
import getBlockTotalReward from 'lib/block/getBlockTotalReward';
import { useI18n } from 'lib/i18n/context';
import getNetworkValidatorTitle from 'lib/networks/getNetworkValidatorTitle';
import Skeleton from 'ui/shared/chakra/Skeleton';
import AddressEntity from 'ui/shared/entities/address/AddressEntity';
import BlockEntity from 'ui/shared/entities/block/BlockEntity';
import IconSvg from 'ui/shared/IconSvg';
import TimeAgoWithTooltip from 'ui/shared/TimeAgoWithTooltip';

type Props = {
  block: Block;
  isLoading?: boolean;
  isLast?: boolean;
};

const LatestBlocksItem = ({ block, isLoading, isLast }: Props) => {
  const { t } = useI18n();
  const totalReward = getBlockTotalReward(block);
  const borderColor = useColorModeValue('#E9ECEF', '#222222');
  const labelColor = useColorModeValue('#616161', '#999999');
  const valueColor = useColorModeValue('#000000', '#FFFFFF');
  const mutedColor = useColorModeValue('#999999', '#616161');
  const itemBg = useColorModeValue('#FFFFFF', '#111111');
  const blockIconSrc = useColorModeValue('/static/homepage/latest-blocks-box-light.svg', '/static/homepage/latest-blocks-box.svg');

  return (
    <Box
      as={ motion.div }
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ display: 'none' }}
      transitionDuration="normal"
      transitionTimingFunction="linear"
      px="16px"
      py="16px"
      minH="138px"
      bg={ itemBg }
      borderBottom={ isLast ? 'none' : '1px solid' }
      borderColor={ borderColor }
    >
      <Flex justifyContent="space-between" alignItems="center" mb="16px" gap="10px">
        <Flex alignItems="center" minW={ 0 }>
          <Image
            src={ blockIconSrc }
            alt=""
            boxSize="32px"
            flexShrink={ 0 }
            mr="8px"
          />
          <BlockEntity
            isLoading={ isLoading }
            number={ block.height }
            tailLength={ 2 }
            truncation="tail"
            noIcon
            fontSize="18px"
            lineHeight="22.5px"
            fontWeight={ 500 }
            fontFamily="'Oxanium', var(--chakra-fonts-body)"
            color={ valueColor }
            _hover={{ color: valueColor, textDecoration: 'none' }}
          />
          { block.celo?.is_epoch_block ? (
            <Tooltip label={ `Finalized epoch #${ block.celo.epoch_number }` }>
              <Box ml="8px">
                <IconSvg name="checkered_flag" boxSize="16px" color={ valueColor } isLoading={ isLoading }/>
              </Box>
            </Tooltip>
          ) : null }
        </Flex>
        <TimeAgoWithTooltip
          timestamp={ block.timestamp }
          enableIncrement={ !isLoading }
          isLoading={ isLoading }
          color={ mutedColor }
          fontWeight={ 500 }
          fontSize="14px"
          lineHeight="17.86px"
          fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
          flexShrink={ 0 }
        />
      </Flex>

      <Flex direction="column" gap="6px">
        <Flex justifyContent="space-between" alignItems="center" gap="10px">
          <Skeleton isLoaded={ !isLoading }>
            <Text
              color={ labelColor }
              fontSize="14px"
              lineHeight="17.86px"
              fontWeight={ 500 }
              fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
            >
              { t('common.txn') }
            </Text>
          </Skeleton>
          <Skeleton isLoaded={ !isLoading }>
            <Text
              color={ valueColor }
              fontSize="14px"
              lineHeight="17.5px"
              fontWeight={ 500 }
              fontFamily="'Oxanium', var(--chakra-fonts-body)"
            >
              { block.transaction_count }
            </Text>
          </Skeleton>
        </Flex>

        { !config.features.rollup.isEnabled && !config.UI.views.block.hiddenFields?.total_reward ? (
          <Flex justifyContent="space-between" alignItems="center" gap="10px">
            <Skeleton isLoaded={ !isLoading }>
              <Text
                color={ labelColor }
                fontSize="14px"
                lineHeight="17.86px"
                fontWeight={ 500 }
                fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
              >
                { t('common.reward') }
              </Text>
            </Skeleton>
            <Skeleton isLoaded={ !isLoading }>
              <Text
                color={ valueColor }
                fontSize="14px"
                lineHeight="17.5px"
                fontWeight={ 500 }
                fontFamily="'Oxanium', var(--chakra-fonts-body)"
              >
                { totalReward.dp(10).toFixed() }
              </Text>
            </Skeleton>
          </Flex>
        ) : null }

        { !config.features.rollup.isEnabled && !config.UI.views.block.hiddenFields?.miner ? (
          <Flex justifyContent="space-between" alignItems="center" gap="10px">
            <Skeleton isLoaded={ !isLoading }>
              <Text
                color={ labelColor }
                fontSize="14px"
                lineHeight="17.86px"
                fontWeight={ 500 }
                fontFamily="'Space Grotesk', var(--chakra-fonts-body)"
              >
                { t(`common.${ getNetworkValidatorTitle() }`) }
              </Text>
            </Skeleton>
            <AddressEntity
              address={ block.miner }
              isLoading={ isLoading }
              noCopy
              truncation="constant"
              fontSize="14px"
              lineHeight="17.5px"
              fontWeight={ 500 }
              fontFamily="'Oxanium', var(--chakra-fonts-body)"
              sx={{
                a: {
                  color: valueColor,
                  _hover: {
                    color: valueColor,
                    textDecoration: 'none',
                  },
                },
                button: {
                  color: '#14DCAC',
                },
              }}
            />
          </Flex>
        ) : null }
      </Flex>
    </Box>
  );
};

export default LatestBlocksItem;
